import { Button, Col, Dropdown, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import React, { SetStateAction, useEffect, useState } from 'react';
import { PackagesEnum } from '../../../../../../enum/PackageEnum';
import ContractModalProfileInfo from './components/ContractModalProfileInfo';
import ContractModalTabs from './components/ContractModalTabs';
import { useTranslation } from 'react-i18next';
import { ContractService } from '../../service';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../../../utils/toasters';
import {
    formatDate,
    getGrandTotal,
    getNumberContentPieces,
    getNumberPosts,
    getStatusIcon,
    getContractStatusLabel,
    isAnnexStatus,
} from '../../../../../../shared/functions/Functions';
import { useLocation } from 'react-router-dom';
import { ConfirmationModal } from '../../../../../../shared/components/modal/ConfirmationModal';
import { useDispatch, useSelector } from 'react-redux';
import { IAllStates } from '../../../../../../store/rootReducer';
import { priceSplitter } from '../../../../../../utils/helpers/priceSplitter';
import { ConfirmSendingOfferModal } from '../../../../../influencer/offer/modal/offer-modal/components/ConfirmSendingOfferModal';
import { DeclineOfferModal } from '../../../../../influencer/offer/modal/offer-modal/components/DeclineOfferModal';
import { gtmService } from '../../../../../../utils/gtm.service';
import HtmlToPdf from '../../../../../../shared/components/generate-pdf';
import { IDeclineContract } from '../dto/IBookingContracts';
import { BookingAcceptContractModal } from '../BookingAcceptContractModal';
import { IContractAnnexEligibility } from '../dto/IContractAnnex';
import { BrandSignAnnexModal } from './components/BrandSignAnnexModal';
import {
    setActiveTab,
    setCampaignId,
    setInitialState,
    setContractModalDetails,
    setAnnexDescription,
    setAnnexMode,
    setContractModalStatus,
    setProfileInfo,
    setUserId,
} from '../../../../../../store/brand/contractBrandSlice';
import { ContractPdfDownloadButton } from '../../../../../../shared/components/pdf/DownloadContractPdf';
import { CheckModalIcon, CrossModalIcon, DeleteIcon, EditIcon, WithdrawIcon } from '../../../../../../shared/Icons';

interface IBookingOfferModal {
    show: boolean;
    closeModal: () => void;
    setDeclineReason?: React.Dispatch<SetStateAction<IDeclineContract>>;
    contractId?: number;
    modalStatus?: string;
    userId?: number;
    passedCampaignId?: number;
    acceptContract?: (contractId: number) => void;
    declineContract?: (contractId: number) => void;
    approveProposal?: (contractId: number) => void;
    declineProposal?: (contractId: number) => void;
    deleteProposal?: (contractId: number) => void;
    selectContract?: (contractId: number, isSelected: boolean) => void;
    setUpdateList?: React.Dispatch<React.SetStateAction<boolean>>;
}

export function BookingContractModal({
    show,
    closeModal,
    contractId,
    userId,
    passedCampaignId,
    modalStatus,
    acceptContract,
    declineContract,
    approveProposal,
    declineProposal,
    deleteProposal,
    selectContract,
    setUpdateList,
    setDeclineReason,
}: IBookingOfferModal) {
    const {
        campaign,
        contractModalStatus,
        isEditable,
        canCounter,
        initialContract,
        isCharity,
        user,
        isTransparentPricing,
        inviteAttachment,
        isAnnexMode,
    } = useSelector((state: IAllStates) => state.contractBrand);

    const [showModal, setShowModal] = useState(false);
    const [showAcceptModal, setShowAcceptModal] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [showInviteConfirmModal, setShowInviteConfirmModal] = useState(false);
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    const [isProposalDecline, setIsProposalDecline] = useState(false);
    const [showDeleteProposalModal, setShowDeleteProposalModal] = useState(false);
    const [showConvertProposalModal, setShowConvertProposalModal] = useState(false);
    const [showSendProposalModal, setShowSendProposalModal] = useState(false);
    const [showApproveProposalModal, setShowApproveProposalModal] = useState(false);
    const [annexEligibility, setAnnexEligibility] = useState<IContractAnnexEligibility | null>(null);
    const [showDeleteAnnexModal, setShowDeleteAnnexModal] = useState(false);
    const [disableButton, setDisableButton] = useState(false);
    const { company, isClient } = useSelector((state: IAllStates) => state.auth.user);
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();
    const campaignId = +location?.pathname?.split('/')?.filter(currLoc => +currLoc)[0];
    const isInvite = modalStatus === 'invite';
    const isNegotiate = contractModalStatus === 'negotiate';
    const contractStatus = campaign?.contracts[0]?.status;
    const isPreAnnex = contractStatus === 'pre_annex';
    const canStartAnnex = !isNegotiate && !!annexEligibility?.canCreate;
    // The button stays visible when the contract cannot be amended, a click explains why
    const annexBlockers = (annexEligibility?.blockers ?? []).filter(blocker => blocker !== 'COMPANY_NOT_ALLOWED');
    const showBlockedAnnex =
        !isNegotiate && !!annexEligibility && !annexEligibility.canCreate && !!annexBlockers.length;
    const canOpenAnnex =
        !isClient && !!company?.canDoAnnex && (contractStatus === 'accepted' || contractStatus === 'completed');
    const canEditProposal = !isClient && campaign?.contracts[0]?.isProposed && !campaign?.contracts[0]?.isApproved;
    const canDeleteProposal = canEditProposal && !campaign?.contracts[0]?.isDeclined;
    const grandTotalSum = getGrandTotal(initialContract.contractItems, initialContract.distribution);
    const grandTotalFeeSum = Math.ceil(grandTotalSum / (campaign?.feePercentage ?? 1));
    const grandTotalForDisplaying = isTransparentPricing
        ? isNegotiate
            ? priceSplitter(grandTotalFeeSum)
            : priceSplitter(campaign?.contracts[0]?.totalWithFee ?? 0)
        : priceSplitter(grandTotalSum);

    useEffect(() => {
        setAnnexEligibility(null);
        if (!show || !canOpenAnnex || !campaign?.contracts[0]?.id) return;
        ContractService.getAnnexEligibility(campaign.contracts[0].id)
            .then(setAnnexEligibility)
            .catch(() => setAnnexEligibility(null));
    }, [show, canOpenAnnex, campaign?.contracts[0]?.id]);

    useEffect(() => {
        dispatch(setInitialState());
        if (show) {
            ContractService.getContract(userId, passedCampaignId ?? campaignId, contractId)
                .then(response => {
                    dispatch(setContractModalDetails(response));
                    // An existing amendment is what the user has to look at
                    if (response.annexBase) {
                        dispatch(setActiveTab('6'));
                    }
                    if (modalStatus === 'negotiate' || isInvite) {
                        dispatch(setContractModalStatus('negotiate'));
                    }
                    if (response.contracts[0]?.user?.id) {
                        dispatch(setProfileInfo(response.contracts[0]?.user));
                    }
                    dispatch(setUserId(userId));
                    dispatch(setCampaignId(campaignId));
                })
                .catch(err => ErrorToast(err));
            if (isInvite) {
                ContractService.getInviteContent(userId).then(response => {
                    if (response) {
                        dispatch(setProfileInfo(response));
                    }
                });
            }
        }
    }, [show]);

    const submitOfferRequest = () => {
        setDisableButton(true);
        let initialOfferCopy = initialContract;
        const contractItems = isCharity
            ? initialOfferCopy.contractItems
            : initialOfferCopy.contractItems.filter(item => item.nbPosts > 0 && item.price > 0);
        const price = getGrandTotal(initialOfferCopy.contractItems, initialOfferCopy.distribution);
        initialOfferCopy = { ...initialOfferCopy, contractItems, price };

        const formData = new FormData();
        const realFiles = Array.isArray(inviteAttachment) ? inviteAttachment.filter(item => item instanceof Blob) : [];
        const hasPlaceholder = Array.isArray(inviteAttachment)
            ? inviteAttachment.some(item => !(item instanceof Blob))
            : false;
        const isFileCleared = realFiles.length === 0 && !hasPlaceholder;

        formData.append(
            'body',
            JSON.stringify({
                ...initialOfferCopy,
                ...(isFileCleared && { clearLegal: true }),
            })
        );

        if (realFiles.length > 0) {
            for (const item of realFiles) {
                formData.append('file', item as Blob);
            }
        }

        const isProposedContract = campaign?.contracts[0]?.isProposed;
        const request = isAnnexMode
            ? isPreAnnex
                ? ContractService.editAnnex(formData, campaign?.contracts[0]?.id ?? 0)
                : ContractService.createAnnex(formData, campaign?.contracts[0]?.id ?? 0)
            : isProposedContract
            ? ContractService.editProposal(formData, contractId)
            : ContractService.submitOffer(formData, userId, campaignId);

        request
            .then(() => {
                if (isAnnexMode) {
                    SuccessToast(t(`brand.campaign.book.contract.modal.annex.${isPreAnnex ? 'updated' : 'created'}`));
                } else if (isProposedContract) {
                    SuccessToast(t('brand.campaign.book.offer.edit_proposal_success'));
                } else {
                    gtmService('brand_offer_submitted', grandTotalForDisplaying);
                }
                setDisableButton(false);
                closeModal();
                if (setUpdateList) {
                    setUpdateList(prevState => !prevState);
                }
            })
            .catch(error => {
                ErrorToast(error);
                setDisableButton(false);
            });
    };

    const inviteUserRequest = () => {
        setDisableButton(true);
        let initialOfferCopy = initialContract;
        const contractItems = isCharity
            ? initialOfferCopy.contractItems
            : initialOfferCopy.contractItems.filter(item => item.nbPosts > 0 && item.price > 0);
        const price = getGrandTotal(initialOfferCopy.contractItems, initialOfferCopy.distribution);
        initialOfferCopy = { ...initialOfferCopy, contractItems, price };

        const formData = new FormData();
        formData.append('body', JSON.stringify(initialOfferCopy));
        if (!!inviteAttachment?.length) {
            for (const item of inviteAttachment) {
                formData.append('file', item as string | Blob);
            }
        }

        ContractService.inviteUser(formData, userId, campaignId)
            .then(() => {
                gtmService('brand_offer_submitted', grandTotalForDisplaying);
                setDisableButton(false);
                SuccessToast(t('brand.campaign.book.invite.successfully_invite_user'));
                closeModal();
                if (setUpdateList) {
                    setUpdateList(prevState => !prevState);
                }
            })
            .catch(error => {
                ErrorToast(error);
                setDisableButton(false);
            });
    };
    const sendProposalRequest = () => {
        setDisableButton(true);
        let initialOfferCopy = initialContract;
        const contractItems = isCharity
            ? initialOfferCopy.contractItems
            : initialOfferCopy.contractItems.filter(item => item.nbPosts > 0 && item.price > 0);
        const price = getGrandTotal(initialOfferCopy.contractItems, initialOfferCopy.distribution);
        initialOfferCopy = { ...initialOfferCopy, contractItems, price };

        const formData = new FormData();
        formData.append('body', JSON.stringify(initialOfferCopy));
        if (!!inviteAttachment?.length) {
            for (const item of inviteAttachment) {
                formData.append('file', item as string | Blob);
            }
        }

        ContractService.sendProposal(formData, userId, campaignId)
            .then(() => {
                SuccessToast(t('brand.campaign.book.invite.successfully_sent_proposal'));
                closeModal();
                if (setUpdateList) {
                    setUpdateList(prevState => !prevState);
                }
            })
            .catch(error => {
                ErrorToast(error);
            })
            .finally(() => {
                setDisableButton(false);
            });
    };
    const withdrawOffer = () => {
        ContractService.withdrawOffer(contractId)
            .then(() => {
                closeModal();
                if (setUpdateList) {
                    setUpdateList(prevState => !prevState);
                }
                SuccessToast(t('brand.campaign.book.offer.modal.successfully_withdrew'));
            })
            .catch(error => ErrorToast(error));
    };
    const declineOfferAction = () => {
        if (isProposalDecline) {
            if (declineProposal) {
                declineProposal(contractId ?? 0);
            }
        } else if (declineContract) {
            declineContract(contractId ?? 0);
        }
        closeModal();
    };

    const approveProposalAction = () => {
        if (approveProposal) {
            approveProposal(contractId ?? 0);
        }
        closeModal();
    };

    const deleteProposalAction = () => {
        if (deleteProposal) {
            deleteProposal(contractId ?? 0);
        } else {
            ContractService.deleteProposal(contractId)
                .then(() => {
                    SuccessToast(t('brand.campaign.book.offer.delete_proposal_success'));
                    if (setUpdateList) {
                        setUpdateList(prevState => !prevState);
                    }
                })
                .catch(error => ErrorToast(error));
        }
        closeModal();
    };

    const convertProposalAction = () => {
        setDisableButton(true);
        ContractService.convertProposalToContract(contractId)
            .then(() => {
                SuccessToast(t('brand.campaign.book.invite.successfully_sent_to_influencer'));
                closeModal();
                if (setUpdateList) {
                    setUpdateList(prevState => !prevState);
                }
            })
            .catch(error => {
                ErrorToast(error);
            })
            .finally(() => {
                setDisableButton(false);
            });
    };

    const closeInternalWithdrawModal = () => {
        setShowModal(false);
    };

    const closeDeclineModal = () => {
        setShowDeclineModal(false);
    };

    // Reloads the contract with the platforms that are not booked yet, so they can be added like in a negotiation
    const openAnnexEditor = (keepDescription: boolean) => {
        ContractService.getContract(userId, passedCampaignId ?? campaignId, contractId, true)
            .then(response => {
                dispatch(setContractModalDetails(response));
                // The editor is the Amendment tab, the contract being amended stays in the Contract tab
                dispatch(setActiveTab('6'));
                dispatch(setAnnexMode(true));
                if (!keepDescription) {
                    dispatch(setAnnexDescription(''));
                }
                dispatch(setContractModalStatus('negotiate'));
            })
            .catch(err => ErrorToast(err));
    };

    const startAnnex = () => openAnnexEditor(false);

    const startAnnexEdit = () => openAnnexEditor(true);

    const deleteAnnexAction = () => {
        setDisableButton(true);
        ContractService.deleteAnnex(campaign?.contracts[0]?.id ?? 0)
            .then(() => {
                SuccessToast(t('brand.campaign.book.contract.modal.annex.deleted'));
                setShowDeleteAnnexModal(false);
                closeModal();
                if (setUpdateList) {
                    setUpdateList(prevState => !prevState);
                }
            })
            .catch(error => ErrorToast(error))
            .finally(() => setDisableButton(false));
    };

    const changeStatus = () => {
        dispatch(setActiveTab('0'));
        dispatch(setContractModalStatus('negotiate'));
    };

    const closeAcceptConfirmModal = () => {
        setShowAcceptModal(false);
        closeModal();
    };
    const closeSubmitModal = () => {
        setShowSubmitModal(false);
    };
    const closeInviteConfirmModal = () => {
        setShowInviteConfirmModal(false);
    };
    const submitForm = (event: any) => {
        event.preventDefault();
        if (isAnnexMode && !initialContract.annexDescription?.trim()) {
            WarningToast(t('brand.campaign.book.contract.modal.annex.annex_description_required'));
            return;
        }
        const hasPlatformPosts = initialContract.contractItems?.some(
            item => item.nbPosts > 0 && (isCharity || item.price > 0)
        );
        if (isAnnexMode && !hasPlatformPosts) {
            WarningToast(t('brand.campaign.book.contract.modal.annex.annex_platforms_required'));
            return;
        }
        isInvite ? setShowInviteConfirmModal(true) : setShowSubmitModal(true);
    };

    return (
        <div onClick={e => e.stopPropagation()}>
            <Modal
                show={show}
                onHide={closeModal}
                id="offer-modal"
                size="xl"
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <form onSubmit={event => submitForm(event)}>
                    <Modal.Body>
                        {!isInvite && campaign?.contracts[0]?.id && (
                            <div
                                className={`offer-status-badge ${campaign?.contracts[0]?.status} ${
                                    isAnnexStatus(campaign?.contracts[0]) ? 'long-label' : ''
                                }`}
                            >
                                {getStatusIcon(campaign?.contracts[0]?.status ?? '')}
                                <p>{getContractStatusLabel(campaign?.contracts[0], t)}</p>
                            </div>
                        )}
                        <Row className="h-100">
                            <Col xl={3} lg={12} className="profile-info pt-2">
                                <ContractModalProfileInfo />
                            </Col>
                            <Col xl={9} lg={12} className="d-flex" style={{ height: 'calc(100% - 64px)' }}>
                                <ContractModalTabs />
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-between justify-content-md-end shadow-top">
                        {isInvite ? (
                            <div
                                onClick={e => e.stopPropagation()}
                                className="d-flex align-items-center justify-content-between w-100"
                            >
                                <Button
                                    variant="outline-primary"
                                    className="btn-sm position-relative"
                                    onClick={() => closeModal()}
                                >
                                    {t('general.button.cancel')}
                                </Button>
                                <div className="d-flex align-items-center">
                                    <div className="d-flex mx-md-4 align-items-center  mr-1 ">
                                        <div className="d-flex align-items-end">
                                            <h4 className="mb-md-0 text-uppercase mr-1 d-md-block d-none">
                                                {t('brand.campaign.book.offer.modal.grand_total')}
                                            </h4>
                                            <h6 className="mb-0 text-uppercase mr-1 d-md-none">
                                                {t('brand.campaign.book.offer.modal.grand_total')}:
                                            </h6>
                                            {!isTransparentPricing && (
                                                <p className="mb-0 d-none d-md-block">
                                                    {company?.package?.name === PackagesEnum.BASIC
                                                        ? t('brand.campaign.book.offer.modal.incl_booking_fee')
                                                        : t('brand.campaign.book.offer.modal.incl_reduced_fee')}
                                                    :
                                                </p>
                                            )}
                                        </div>
                                        <h4 className="text-primary m-0 px-2 d-none d-md-block euro-symbol">
                                            {grandTotalForDisplaying}
                                        </h4>
                                        <h6 className="text-primary m-0 pl-2 d-md-none euro-symbol">
                                            {grandTotalForDisplaying}
                                        </h6>
                                    </div>
                                    {campaign?.contractPlaceholder?.flags?.canSentOfferProposal ? (
                                        <Dropdown>
                                            <Dropdown.Toggle
                                                className="btn-sm"
                                                variant="primary"
                                                id="dropdown-invite-respond"
                                                disabled={disableButton}
                                            >
                                                {t('general.button.actions')}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item as="button" type="submit" disabled={disableButton}>
                                                    <div className="d-flex align-items-center">
                                                        <i className="fi fi-rr-file-export font-14 me-2 icon-fixed"></i>
                                                        {t('brand.campaign.book.invite.invite')}
                                                    </div>
                                                </Dropdown.Item>
                                                <Dropdown.Item
                                                    onClick={() => setShowSendProposalModal(true)}
                                                    disabled={disableButton}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <i className="fi fi-rr-add font-14 me-2 icon-fixed"></i>
                                                        {t('brand.campaign.book.invite.send_proposal')}
                                                    </div>
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    ) : (
                                        <Button
                                            className="btn-sm position-relative"
                                            type="submit"
                                            disabled={disableButton}
                                        >
                                            {t('brand.campaign.book.invite.invite')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex flex-column-reverse flex-md-row justify-content-between w-100">
                                <Button
                                    variant="outline-primary"
                                    className="btn-sm position-relative ml-md-3 mt-2 mt-md-0 text-uppercase"
                                    onClick={() => closeModal()}
                                >
                                    {t('general.button.cancel')}
                                </Button>
                                <div className="d-flex align-items-center justify-content-between" style={{ gap: 8 }}>
                                    <ContractPdfDownloadButton
                                        contractId={campaign?.contracts[0]?.id}
                                        status={campaign?.contracts[0]?.status}
                                        isZip={true}
                                    />
                                    {/*{(campaign?.contracts?.[0]?.status === 'accepted' ||*/}
                                    {/*    campaign?.contracts?.[0]?.status === 'completed') && (*/}
                                    {/*    <HtmlToPdf*/}
                                    {/*        passedCampaignId={passedCampaignId ?? campaignId}*/}
                                    {/*        passedOffers={campaign?.contracts[0].id}*/}
                                    {/*    />*/}
                                    {/*)}*/}
                                    <div className="d-md-flex mx-md-4 align-items-center mr-1">
                                        <div className="d-flex align-items-end">
                                            <h4 className="mb-md-0 text-uppercase mr-1 d-md-block d-none">
                                                {t('brand.campaign.book.offer.modal.grand_total')}
                                            </h4>
                                            <h6 className="mb-0 text-uppercase mr-1 d-md-none">
                                                {t('brand.campaign.book.offer.modal.grand_total')}
                                            </h6>
                                            {!isTransparentPricing && (
                                                <p className="mb-0 d-none d-md-block">
                                                    {company?.package?.name === PackagesEnum.BASIC
                                                        ? t('brand.campaign.book.offer.modal.incl_booking_fee')
                                                        : t('brand.campaign.book.offer.modal.incl_reduced_fee')}
                                                    :
                                                </p>
                                            )}
                                        </div>
                                        <h4 className="text-primary m-0 px-2 d-none d-md-block euro-symbol">
                                            {grandTotalForDisplaying}
                                        </h4>
                                        <h6 className="text-primary m-0 px-2 d-md-none euro-symbol">
                                            {grandTotalForDisplaying}
                                        </h6>
                                    </div>
                                    {!isNegotiate &&
                                        !isClient &&
                                        campaign?.contracts[0]?.isProposed &&
                                        campaign?.contracts[0]?.isApproved &&
                                        !campaign?.contracts[0]?.isConvertedToOffer && (
                                            <Button
                                                variant="primary"
                                                className="btn-sm position-relative mr-2"
                                                disabled={disableButton}
                                                onClick={() => setShowConvertProposalModal(true)}
                                            >
                                                {t('brand.campaign.book.invite.send_offer_to_influencer')}
                                            </Button>
                                        )}
                                    {campaign?.contracts[0]?.isProposed ? (
                                        <>
                                            {!isNegotiate &&
                                                isClient &&
                                                !campaign?.contracts[0]?.isApproved &&
                                                !campaign?.contracts[0]?.isDeclined && (
                                                    <Dropdown>
                                                        <Dropdown.Toggle
                                                            className="mr-2 btn-outline-primary btn-sm"
                                                            variant="light"
                                                            id="dropdown-basic"
                                                        >
                                                            {t('general.button.actions')}
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu>
                                                            <Dropdown.Item
                                                                id="approve-proposal"
                                                                onClick={() => setShowApproveProposalModal(true)}
                                                            >
                                                                <div className="d-flex align-items-center">
                                                                    <CheckModalIcon
                                                                        width={14}
                                                                        height={14}
                                                                        className="me-2"
                                                                    />
                                                                    {t('brand.campaign.book.offer.approve_proposal')}
                                                                </div>
                                                            </Dropdown.Item>
                                                            <Dropdown.Item
                                                                id="decline-proposal"
                                                                onClick={() => {
                                                                    setIsProposalDecline(true);
                                                                    setShowDeclineModal(true);
                                                                }}
                                                            >
                                                                <div className="d-flex align-items-center">
                                                                    <CrossModalIcon
                                                                        width={14}
                                                                        height={14}
                                                                        className="me-2"
                                                                    />
                                                                    {t('brand.campaign.book.offer.decline_proposal')}
                                                                </div>
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                )}
                                            {!isNegotiate && canEditProposal && (
                                                <Dropdown>
                                                    <Dropdown.Toggle
                                                        className="mr-2 btn-sm"
                                                        variant="outline-primary"
                                                        id="dropdown-proposal-respond"
                                                    >
                                                        {t('general.button.actions')}
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item id="edit-proposal" onClick={changeStatus}>
                                                            <EditIcon
                                                                width={20}
                                                                height={20}
                                                                className="dropdown-menu-icon"
                                                            />
                                                            {t('brand.campaign.book.offer.edit_proposal')}
                                                        </Dropdown.Item>
                                                        {canDeleteProposal && (
                                                            <Dropdown.Item
                                                                className="text-danger"
                                                                id="delete-proposal"
                                                                onClick={() => setShowDeleteProposalModal(true)}
                                                            >
                                                                <DeleteIcon
                                                                    width={20}
                                                                    height={20}
                                                                    className="dropdown-menu-icon"
                                                                />
                                                                {t('brand.campaign.book.offer.delete_proposal')}
                                                            </Dropdown.Item>
                                                        )}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            )}
                                        </>
                                    ) : isClient ||
                                      (!campaign?.contractPlaceholder?.flags?.canWithdraw &&
                                          !canCounter &&
                                          !isPreAnnex) ||
                                      isNegotiate ? null : (
                                        <Dropdown>
                                            <Dropdown.Toggle
                                                className="mr-2 btn-outline-primary btn-sm"
                                                variant="light"
                                                id="dropdown-basic"
                                            >
                                                {isPreAnnex ? t('general.button.actions') : t('general.button.respond')}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                {/* {canCounter && (
                                                    <Dropdown.Item
                                                        onClick={() =>
                                                            selectContract &&
                                                            (selectContract(
                                                                contractId ?? 0,
                                                                !campaign?.contracts[0]?.isSelected
                                                            ),
                                                            closeModal())
                                                        }
                                                        id="addToCart"
                                                    >
                                                        {campaign?.contracts[0]?.isSelected
                                                            ? t('general.button.remove_from_cart')
                                                            : t('general.button.add_to_cart')}
                                                    </Dropdown.Item>
                                                )} */}
                                                {isPreAnnex && (
                                                    <>
                                                        <Dropdown.Item id="edit-annex" onClick={startAnnexEdit}>
                                                            <div className="d-flex align-items-center">
                                                                <EditIcon
                                                                    width={20}
                                                                    height={20}
                                                                    className="dropdown-menu-icon"
                                                                />
                                                                {t('general.button.edit')}
                                                            </div>
                                                        </Dropdown.Item>
                                                        <Dropdown.Item
                                                            className="text-danger"
                                                            id="delete-annex"
                                                            onClick={() => setShowDeleteAnnexModal(true)}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <DeleteIcon
                                                                    width={20}
                                                                    height={20}
                                                                    className="dropdown-menu-icon"
                                                                />
                                                                {t('general.button.delete')}
                                                            </div>
                                                        </Dropdown.Item>
                                                    </>
                                                )}
                                                {isEditable && (
                                                    <Dropdown.Item id="negotiate" onClick={changeStatus}>
                                                        <div className="d-flex align-items-center">
                                                            <i className="fi fi-rr-messages-question icon-fixed me-2"></i>
                                                            {t('general.button.negotiate')}
                                                        </div>
                                                    </Dropdown.Item>
                                                )}
                                                {campaign?.contractPlaceholder?.flags?.canWithdraw && (
                                                    <Dropdown.Item onClick={() => setShowModal(true)} id="withdraw">
                                                        <div className="d-flex align-items-center">
                                                            <WithdrawIcon width={16} height={16} className="me-2" />
                                                            {t('brand.campaign.book.contract.modal.withdraw_contract')}
                                                        </div>
                                                    </Dropdown.Item>
                                                )}
                                                {canCounter && (
                                                    <Dropdown.Item
                                                        id="decline"
                                                        onClick={() => {
                                                            setIsProposalDecline(false);
                                                            setShowDeclineModal(true);
                                                        }}
                                                        className="d-flex align-items-center danger-hover"
                                                    >
                                                        <CrossModalIcon width={16} height={16} className="me-2" />
                                                        {t('general.button.decline')}
                                                    </Dropdown.Item>
                                                )}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    )}
                                    {canStartAnnex && !isClient && (
                                        <Button
                                            className="mr-2 btn-outline-primary btn-sm"
                                            variant="light"
                                            id="annex"
                                            onClick={startAnnex}
                                        >
                                            {t('brand.campaign.book.contract.modal.annex.title')}
                                        </Button>
                                    )}
                                    {showBlockedAnnex && !isClient && (
                                        <OverlayTrigger
                                            trigger="click"
                                            rootClose
                                            placement="top"
                                            overlay={
                                                <Tooltip id="annex-blocked-tooltip">
                                                    {annexBlockers.map(blocker => (
                                                        <div key={blocker}>
                                                            {t(
                                                                `brand.campaign.book.contract.modal.annex.blocked.${blocker}`
                                                            )}
                                                        </div>
                                                    ))}
                                                </Tooltip>
                                            }
                                        >
                                            <Button
                                                className="mr-2 btn-outline-primary btn-sm"
                                                style={{ opacity: 0.65, cursor: 'not-allowed' }}
                                                variant="light"
                                                id="annex"
                                                aria-disabled="true"
                                            >
                                                {t('brand.campaign.book.contract.modal.annex.title')}
                                            </Button>
                                        </OverlayTrigger>
                                    )}
                                    <Button
                                        type="submit"
                                        className={`${
                                            ((canCounter && !isClient) || canEditProposal || isAnnexMode) && isNegotiate
                                                ? ''
                                                : 'd-none'
                                        } btn-sm position-relative mr-2`}
                                    >
                                        {isAnnexMode
                                            ? t('brand.campaign.book.contract.sign')
                                            : t('general.button.submit')}
                                    </Button>
                                    {canCounter && !isClient && !campaign?.contracts[0]?.isExpired ? (
                                        <Button
                                            className={`btn-sm position-relative ${isNegotiate && 'd-none'} mr-md-3`}
                                            onClick={() => setShowAcceptModal(true)}
                                        >
                                            {t('general.button.accept')}
                                        </Button>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </Modal.Footer>
                </form>
                {isAnnexMode ? (
                    <BrandSignAnnexModal
                        action={submitOfferRequest}
                        grandTotal={grandTotalForDisplaying}
                        show={showSubmitModal}
                        disableButton={disableButton}
                        closeModal={closeSubmitModal}
                        nbPosts={getNumberContentPieces(initialContract.contractItems)}
                        user={campaign?.contracts[0]?.user ?? user}
                    />
                ) : (
                    <ConfirmSendingOfferModal
                        action={submitOfferRequest}
                        grandTotal={grandTotalForDisplaying}
                        show={showSubmitModal}
                        disableButton={disableButton}
                        closeModal={closeSubmitModal}
                        nbPosts={getNumberContentPieces(initialContract.contractItems)}
                        user={user}
                        isContract={true}
                        isProposal={canEditProposal}
                    />
                )}
                <ConfirmSendingOfferModal
                    action={approveProposalAction}
                    grandTotal={grandTotalForDisplaying}
                    show={showApproveProposalModal}
                    disableButton={disableButton}
                    closeModal={() => setShowApproveProposalModal(false)}
                    nbPosts={getNumberContentPieces(initialContract.contractItems)}
                    user={user}
                    isContract={true}
                    isProposalApproval={true}
                />
                <ConfirmSendingOfferModal
                    action={sendProposalRequest}
                    grandTotal={grandTotalForDisplaying}
                    show={showSendProposalModal}
                    disableButton={disableButton}
                    closeModal={() => setShowSendProposalModal(false)}
                    nbPosts={getNumberContentPieces(initialContract.contractItems)}
                    user={user}
                    isContract={true}
                    isProposal={true}
                />
                <ConfirmSendingOfferModal
                    action={convertProposalAction}
                    grandTotal={grandTotalForDisplaying}
                    show={showConvertProposalModal}
                    disableButton={disableButton}
                    closeModal={() => setShowConvertProposalModal(false)}
                    nbPosts={getNumberContentPieces(initialContract.contractItems)}
                    user={user}
                    isContract={true}
                />
                <ConfirmationModal
                    show={showDeleteProposalModal}
                    closeModal={() => setShowDeleteProposalModal(false)}
                    action={deleteProposalAction}
                    title={t('brand.campaign.book.offer.delete_proposal')}
                    description={t('brand.campaign.book.offer.delete_proposal_confirm')}
                />
                <ConfirmationModal
                    title={t('brand.campaign.book.contract.modal.withdraw_contract_question')}
                    description={` ${t('brand.campaign.book.contract.modal.withdraw_description')} ${formatDate(
                        campaign?.contracts[0]?.createdAt ?? ''
                    )} - ${campaign?.contracts[0]?.createdAt.substring(11, 16)}! ${t(
                        'brand.campaign.book.offer.modal.are_you_sure'
                    )} `}
                    action={withdrawOffer}
                    show={showModal}
                    closeModal={closeInternalWithdrawModal}
                />
                <ConfirmationModal
                    show={showDeleteAnnexModal}
                    closeModal={() => setShowDeleteAnnexModal(false)}
                    action={deleteAnnexAction}
                    title={t('brand.campaign.book.contract.modal.annex.delete_question')}
                    description={t('brand.campaign.book.contract.modal.annex.delete_description')}
                />
                <BookingAcceptContractModal
                    action={acceptContract}
                    contractId={contractId}
                    show={showAcceptModal}
                    closeModal={closeAcceptConfirmModal}
                    grandTotal={grandTotalForDisplaying}
                    nbPosts={getNumberPosts(campaign?.contractPlaceholder?.totalPlatforms)}
                    user={campaign?.contracts[0]?.user}
                    sign={true}
                />
                <DeclineOfferModal
                    date={formatDate(campaign?.contracts[0]?.createdAt ?? '')}
                    declineOffer={declineOfferAction}
                    show={showDeclineModal}
                    closeModal={closeDeclineModal}
                    setDeclineReason={setDeclineReason}
                    isContract={true}
                />
                <ConfirmSendingOfferModal
                    action={inviteUserRequest}
                    grandTotal={grandTotalForDisplaying}
                    show={showInviteConfirmModal}
                    disableButton={disableButton}
                    closeModal={closeInviteConfirmModal}
                    nbPosts={getNumberContentPieces(initialContract.contractItems)}
                    user={user}
                    isContract={true}
                />
            </Modal>
        </div>
    );
}
